/**
 * The game on screen (docs/spec/04 §2, §6): runs the engine at 60 ticks a second, turns its events
 * into sound and effects, draws it, and sees the game out to the results and the tables.
 */

import type { GameAudio } from '../../audio/types';
import { dailySeed, dateKey } from '../../game/daily';
import type { TableId } from '../../game/hiscores';
import type { Controls } from '../../input/controls';
import { Fx } from '../../render/effects/fx';
import { drawField } from '../../render/fieldView';
import { drawChronoFrame, drawHud, drawResults } from '../../render/hud';
import { Motion } from '../../render/motion';
import type { Screen } from '../../render/screen';
import { drawGamePanels } from '../../render/sidePanels';
import { Starfield } from '../../render/starfield';
import type { GameState, Input } from '../../sim';
import { createGame, FLOW, step, TICKS_PER_SECOND } from '../../sim';
import type { MenuAction, PlayRequest, PointerInput, Scene, SceneHost } from '../../ui/scene';
import { announcement, musicCue } from './cues';
import { PauseMenu } from './pauseMenu';

export interface GameDeps {
  readonly audio: GameAudio;
  readonly controls: Controls;
  /**
   * A fresh autopilot. `showman`: the attract demo's, which plays like a person rather than a
   * machine, so that the hive gets to form and dive (`window.__game.bot` gets the plain one).
   */
  pilot(showman: boolean): (s: GameState) => Input;
  /** Show or hide the touch controls; `nova` for the Nova button. */
  touch(visible: boolean, nova: boolean): void;
  /** A seed for a game that was not given one. */
  seed(): number;
}

const TICK_MS = 1000 / TICKS_PER_SECOND;
/** The results stay up this long unless a button moves things on. */
const RESULTS_MS = 12_000;
/**
 * At most this many ticks a frame. The host never hands a frame more than 100 ms, which is six
 * ticks, so a frame that ran long is caught up in full and the game never drops into slow motion;
 * a tab that was asleep for longer simply carries on from where it was.
 */
const MAX_CATCH_UP = 8;

export class GameScene implements Scene {
  private readonly s: GameState;
  private readonly fx = new Fx();
  private readonly motion = new Motion();
  private readonly stars = new Starfield();
  private readonly pause = new PauseMenu();
  private readonly demo: boolean;
  private bot: ((s: GameState) => Input) | null = null;
  private acc = 0;
  private paused = false;
  private started = false;
  /** Milliseconds the results have been up; -1 before. */
  private resultsMs = -1;
  private done = false;

  constructor(private readonly host: SceneHost, private readonly request: PlayRequest, private readonly deps: GameDeps) {
    this.demo = request.mode === 'demo';
    const daily = request.mode === 'daily';
    const seed = daily ? dailySeed(host.services.today()) : (request.seed ?? deps.seed());
    this.s = createGame({
      mode: request.mode === 'classic' ? 'classic' : 'supernova',
      seed,
      ...(request.stage !== undefined ? { startStage: request.stage } : {}),
      ...(request.fighters !== undefined ? { fighters: request.fighters } : {}),
    });
    if (this.demo) this.bot = deps.pilot(true);
  }

  private get table(): TableId {
    return this.s.mode === 'classic' ? 'classic' : 'supernova';
  }

  enter(): void {
    const { audio, controls } = this.deps;
    controls.inGame = !this.paused;
    this.deps.touch(!this.demo, this.s.mode === 'supernova');
    if (this.started) return;
    this.started = true;
    audio.music(this.demo ? null : 'start');
    this.react();
  }

  leave(): void {
    this.deps.controls.inGame = false;
    this.deps.touch(false, false);
    this.deps.audio.hush();
  }

  /** Use the autopilot (or hand the stick back). */
  setBot(on: boolean): void {
    this.bot = on ? this.deps.pilot(false) : null;
  }

  private fxOptions(): { particles: boolean; shake: boolean; reduceFlashing: boolean } {
    const o = this.host.services.options.get();
    return { particles: o.particles, shake: o.shake, reduceFlashing: o.reduceFlashing };
  }

  /** Turn the tick's events into sound, effects, music and words for a screen reader. */
  private react(): void {
    const { audio } = this.deps;
    const events = this.s.events;
    if (events.length === 0) return;
    if (!this.demo) audio.events(events);
    this.fx.events(events, this.fxOptions());
    for (const e of events) {
      const cue = musicCue(e);
      if (cue !== undefined && !this.demo) audio.music(cue);
      const words = announcement(e);
      if (words && !this.demo) this.host.services.announce(words);
    }
  }

  private tick(): void {
    const s = this.s;
    const turbo = this.host.services.options.get().turbo;
    const held = this.deps.controls.keys.take(turbo);
    const chrono = s.powers.chrono > 0;
    this.motion.before(s);
    step(s, this.bot ? this.bot(s) : held);
    this.motion.after(s, chrono);
    this.react();
    this.fx.update(s, this.fxOptions());
    if (!this.demo) this.deps.audio.ambience({ beam: s.beam !== null, lance: s.empress?.lance === 'fire' });
    if (s.phase === 'gameover' && s.phaseT >= FLOW.gameOverTicks && this.resultsMs < 0) this.resultsMs = 0;
  }

  update(dtMs: number): void {
    if (this.paused || this.done) return;
    const s = this.s;
    // Chrono slows the sky with the hive, so the slow-down reads as time bending, not as lag.
    this.stars.update(dtMs, s.powers.chrono > 0 ? 0.3 : s.phase === 'intro' || s.phase === 'clear' ? 3 : 1);
    if (this.resultsMs >= 0) {
      this.resultsMs += dtMs;
      if (this.resultsMs >= RESULTS_MS || this.demo) this.finish();
      return;
    }
    this.acc += dtMs;
    let n = 0;
    while (this.acc >= TICK_MS && n < MAX_CATCH_UP) {
      this.acc -= TICK_MS;
      this.tick();
      n++;
    }
    if (n === MAX_CATCH_UP) this.acc = 0;
  }

  /** The game is over and the results have been read: on to the initials, the table or the title. */
  private finish(): void {
    if (this.done) return;
    this.done = true;
    const { host, s } = this;
    const scores = host.services.scores;
    if (this.demo) return host.go({ to: 'title', skipPress: true });
    if (this.request.mode === 'daily') {
      scores.recordDaily(dateKey(host.services.today()), s.score, s.stage);
      return host.go({ to: 'daily' });
    }
    if (scores.rank(this.table, s.score) >= 0) {
      return host.go({ to: 'initials', entry: { table: this.table, score: s.score, stage: s.stage } });
    }
    host.go({ to: 'hiscores', table: this.table });
  }

  private setPaused(on: boolean): void {
    if (this.demo || this.resultsMs >= 0 || this.paused === on) return;
    this.paused = on;
    this.deps.controls.inGame = !on;
    this.deps.controls.reset();
    if (on) {
      this.pause.reset();
      this.deps.audio.hush();
    }
    this.host.services.announce(on ? 'Paused' : 'Resumed');
  }

  /** The window lost focus. */
  autoPause(): void {
    if (this.s.phase !== 'gameover') this.setPaused(true);
  }

  private choose(choice: 'resume' | 'options' | 'quit'): void {
    this.deps.audio.ui('select');
    if (choice === 'resume') this.setPaused(false);
    else if (choice === 'options') this.host.push({ to: 'options' });
    else this.host.go({ to: 'title', skipPress: true });
  }

  action(a: MenuAction): void {
    if (this.demo) return this.finish();
    if (this.resultsMs >= 0) {
      if (a === 'confirm' && this.resultsMs > 600) this.finish();
      return;
    }
    if (!this.paused) {
      if (a === 'pause' || a === 'back') this.setPaused(true);
      return;
    }
    if (a === 'pause' || a === 'back') return this.setPaused(false);
    const { moved, choice } = this.pause.action(a);
    if (moved) this.deps.audio.ui('move');
    if (choice) this.choose(choice);
  }

  pointer(p: PointerInput): void {
    if (p.kind !== 'down' && !this.paused) return;
    if (this.demo) return this.finish();
    if (this.resultsMs > 600 && p.kind === 'down') return this.finish();
    if (!this.paused) return;
    const choice = this.pause.pointer(p);
    if (choice) this.choose(choice);
  }

  draw(screen: Screen): void {
    const s = this.s;
    const o = this.host.services.options.get();
    screen.clear(0);
    this.stars.draw(screen, o.deepStars, o.reduceFlashing);
    const off = this.paused ? { x: 0, y: 0 } : this.fx.shakeOffset();
    const ctx = screen.ctx;
    ctx.save();
    ctx.translate(off.x, off.y);
    // Between ticks, things are drawn part way from where they were to where they are.
    this.motion.alpha = this.paused || this.resultsMs >= 0 ? 1 : Math.min(1, this.acc / TICK_MS);
    drawField(screen, s, this.motion);
    this.fx.draw(screen);
    ctx.restore();
    if (s.powers.chrono > 0) drawChronoFrame(screen, s.powers.chrono, s.tick);
    const results = this.resultsMs >= 0;
    if (results) screen.faded(0.85, () => screen.fill(0, 16, screen.width, screen.height - 32, 0));
    drawHud(screen, s, { hiScore: this.host.services.scores.best(this.table), demo: this.demo, calm: o.reduceFlashing, results });
    if (results) drawResults(screen, s);
    if (this.paused) this.pause.draw(screen);
  }

  /** The instruction card and the gauges, either side of the screen on a wide window. */
  sides(left: Screen, right: Screen): void {
    const { options, input } = this.host.services;
    const o = options.get();
    const name = (code: string): string => input.keyName(code);
    const modes = { supernova: 'SUPERNOVA', classic: 'CLASSIC 1981', daily: 'DAILY RUN', demo: 'DEMO' } as const;
    drawGamePanels(left, right, this.s, {
      modeName: modes[this.request.mode],
      keys: {
        left: name(o.keys.left[0]),
        right: name(o.keys.right[0]),
        fire: name(o.keys.fire[0]),
        bomb: name(o.keys.bomb[0]),
        pause: name(o.keys.pause[0]),
      },
      calm: o.reduceFlashing,
    });
  }

  /** A summary of the game, for `window.__game.state()` and the browser tests. */
  debugState(): Record<string, unknown> {
    const s = this.s;
    return {
      mode: s.mode,
      tick: s.tick,
      stage: s.stage,
      stageKind: s.stageKind,
      phase: s.phase,
      score: s.score,
      lives: s.lives,
      enemies: s.enemies.length,
      formed: s.enemies.filter((e) => e.state === 'formed').length,
      player: { x: s.player.x, status: s.player.status, dual: s.player.dual },
      shots: s.stats.shots,
      hits: s.stats.hits,
      chain: s.chain,
      novas: s.powers.novas,
      empress: s.empress ? { hp: s.empress.hp, state: s.empress.state } : null,
      paused: this.paused,
      results: this.resultsMs >= 0,
      bot: this.bot !== null,
    };
  }

  /** The engine's state itself, for a console. */
  get state(): GameState {
    return this.s;
  }
}
